<template>
  <div class="container py-5">

    <div class="level">
      <div class="level-left flex flex--row flex--justify-space-between mb-0 mx-2">
        <div class="title is-4 mr-3 ml-1 mb-0">
          Skids
        </div>
        <div class="buttons hide-largescreen">
          <button
            v-if="can.create"
            class="button is-primary"
            @click="creatingSkid = true">
            Create a Skid
          </button>
          <button
            @click="download" 
            class="button">
            <span class="icon">
              <i class="fas fa-download"></i>
            </span>
          </button>
        </div>
      </div>

      <div class="level-right buttons hide-smallscreen flex--align-center">
        <v-fade-transition>
          <button
            v-if="selected.length > 0" 
            class="button mb-0" 
            @click="launchConfirmDelete()">
            <span class="icon is-medium">
              <i class="fas fa-trash has-text-danger"></i>
            </span>
            <span>
              Delete {{ selected.length }} Selected Skids
            </span>
          </button>
        </v-fade-transition>

        <button
          v-if="can.create"
          class="button is-primary mb-0"
          @click="creatingSkid = true">
          Create a Skid
        </button>

        <button
          @click="download" 
          class="button mb-0">
          <span class="icon">
            <i class="fas fa-download"></i>
          </span>
        </button>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <search-autocomplete
          :value="filters.project_id" 
          @input="changeFilter('project_id', $event)"
          @updateProject="setProject($event)"/>
      </div>
      <div class="column">
        <v-search
          :value="filters.skid_no"
          :search-input.sync="filters.skid_no"
          @search="changeFilter('skid_no', $event)"
          @clear="changeFilter('skid_no', null)"
          placeholder="Search by Skid/ Group #"/>
      </div>
      <div class="column">      
        <v-search
          :value="filters.piece_no"
          :search-input.sync="filters.piece_no"
          @search="changeFilter('piece_no', $event)"
          @clear="changeFilter('piece_no', null)"
          placeholder="Search by Piece #"/>
      </div>
      <div class="column">
        <v-switcher
          v-if="can.create"
          :value="filters.is_sent"
          @input="changeFilter('is_sent', $event)"
          label="Only Sent Skids"/>
      </div>
    </div>

    <div class="hide-largescreen px-3 pb-3">
      <label class="label">Filters</label>

      <div class="field is-grouped is-grouped-multiline">
        <div 
          v-for="(filterValue, filterName) in activeFilters" 
          :key="filterName"
          class="control">
          <div 
            class="tags has-addons">
            <span class="tag is-primary">
              <span class="pr-1">{{ filterNameMap[filterName] }}:</span>
              <span v-if="filterName != 'project_id'"> {{ filterValue }}</span>
              <span v-if="filterName == 'project_id' && project"> {{ project.project_name }}</span>
            </span>
            <a 
              @click="changeFilter(filterName, null)" 
              class="tag is-delete">
            </a>
          </div>
        </div>
      </div>

      <button
        @click="addingFilters = true" 
        class="button">
        Add Filters
      </button>
    </div>

    <article 
      v-if="!loading && skids.length == 0" 
      class="message is-info is-fullwidth mt-3">
      <div class="message-body">
        There are no skids.
      </div>
    </article>

    <table 
      v-if="loading || skids.length > 0"
      class="table is-hoverable is-clickable is-fullwidth is-responsive has-small-font mt-3">

      <thead>
        <tr>
          <th>
            <v-checkbox 
              :value="allSelected"
              @input="toggleAllSelected"/>
          </th>
          <th 
            v-for="(header, headerIndex) in headers" 
            :key="headerIndex">
            {{ header.label }}
          </th>
          <th v-if="can.delete || can.update"></th>
        </tr>
        <tr v-if="loading">
          <th :colspan="headers.length + 2" class="py-0 px-0 is-naked">
            <v-progress class="is-fullwidth" v-model="loading"/>
          </th>
        </tr>
      </thead>

      <tbody v-if="skids.length > 0">
        <tr 
          class="pointer v-centered"
          v-for="skid in skids"
          :key="skid.id"
          @click="can.create ? viewSkid(skid) : viewSkidStatus(skid)">
          <td class="hide-smallscreen" @click.stop="">
            <v-checkbox 
              :value="selected.includes(skid.id)"
              @input="toggleSelected(skid.id)"/>
          </td>
          <td data-title="Project Name">{{ skid.project.project_name }}</td>
          <td data-title="Skid/ Group #">{{ skid.skid_no }}</td>
          <td 
            :class="{ 'hide-smallscreen': !skid.desc }" 
            data-title="Skid Description">
            {{ skid.desc }}
          </td>
          <td data-title="Packed On">
            <v-datepicker-modal-slot 
              @click.native.stop=""
              has-time
              v-if="!skid.packed_on"
              :value="skid.packed_on"
              @input="updateSkid({ 
                skidId: skid.id,
                data: { packed_on: $event }
              })">
              <a>
                Mark Skid as Packed
              </a>
            </v-datepicker-modal-slot>
            <span v-else>
              {{ format(parse(skid.packed_on)) }}
            </span> 
          </td>
          <td data-title="Sent On">
            <v-datepicker-modal-slot 
              @click.native.stop=""
              has-time
              v-if="!skid.is_sent"
              :value="skid.sent_on"
              @input="updateSkid({ 
                skidId: skid.id,
                data: { is_sent: true, sent_on: $event }
              })">
              <a>
                Mark Skid as Sent
              </a>
            </v-datepicker-modal-slot>
            <span v-else>
              {{ format(parse(skid.sent_on)) }}
            </span> 
          </td>
          <td 
            data-title="Number of Items"
            :class="{ 'has-text-grey-light': !skid.no_items || skid.no_items == 0 }">
            {{ skid.no_items }}
          </td>
          <td data-title="Installation Progress">
            <div v-if="skid.total_sqft != 0">
              <div class="has-text-weight-bold text--nowrap">
                {{ skid.installed_sqft | commasep(0) }} / 
                {{ skid.total_sqft | commasep(0) }} 
                ({{ (skid.installed_sqft / skid.total_sqft) | percentage }})
              </div>
              <div class="has-text-grey">
                SQFT Installed 
              </div>
            </div>
            <div v-else class="has-text-grey-light">
              0 Total SQFT
            </div>
          </td>
          <td data-title="Created By/ On">
            <div>{{ skid.created_user.display_name }}</div>
            <div class="has-text-grey is-size-7 text--nowrap">
              {{ format(parse(skid.created_on)) }}
            </div>
          </td>
          <td>
            <div class="buttons buttons-right">
              <button
                v-if="can.create && can.update"
                @click.stop="viewSkidStatus(skid)" 
                class="button is-small">
                <span>View Items Status</span>
              </button>
              <button
                v-if="can.delete"
                @click.stop="launchConfirmDelete(skid.id)" 
                class="button is-small">
                <span class="icon">
                  <i class="fas fa-trash has-text-grey"></i>
                </span>
              </button>
            </div>
          </td>
        </tr>
      </tbody>

    </table>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

    <create-skid-modal v-model="creatingSkid"/>

    <add-skid-filters-modal v-model="addingFilters"/>

  </div>
</template>

<script>
import VPagination from '@/components/base/VPagination'
import VConfirmModal from '@/components/base/VConfirmModal'
import VCheckbox from '@/components/base/VCheckbox'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import VProgress from '@/components/base/VProgress'
import VDatepickerModalSlot from '@/components/base/VDatepickerModalSlot'
import VSearch from '@/components/base/VSearch'
import VSwitcher from '@/components/base/VSwitcher'

import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import AddSkidFiltersModal from '@/components/installationSkids/AddSkidFiltersModal'
import CreateSkidModal from '@/components/skids/CreateSkidModal'

import { mapState, mapActions, mapMutations } from 'vuex'
import { parseISO, format } from 'date-fns'
import { downloadCsv } from '@/mixins/downloadable.js'

export default {
  components: { 
    VPagination, VConfirmModal, VCheckbox, VFadeTransition, VProgress, 
    VDatepickerModalSlot, VSearch, SearchAutocomplete, CreateSkidModal,
    AddSkidFiltersModal, VSwitcher
  },
  data() {
    return {
      headers: [
        { label: 'Skid Project' },
        { label: 'Skid Number' },
        { label: 'Skid Description' },
        { label: 'Packed On' },
        { label: 'Sent On' },
        { label: '# of Items' },
        { label: 'Installation Progress' },
        { label: 'Created By/ On' }
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this skid?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      },
      formatCsvDate: (d) => {
        return d ? format(d, 'yyyy-MM-dd_H.mm.ss') : null
      },
      creatingSkid: false,
      filterNameMap: {
        "project_id": "Project", 
        "piece_no": "Piece #",
        "is_sent": "Is Sent"
      },
      addingFilters: false
    }
  },
  mixins: [downloadCsv],
  computed: {
    ...mapState("skidInstallations", [
      "skids", "loading", "selected", "pagination", "filters", "allSelected",
      "project"
    ]),
    ...mapState("auth", ["user"]),
    can({ user }) {
      return {
        update: user.role == 'installer' || user.role == 'ceo' || user.permission == 'admin',
        create: user.role == 'office-manager' || user.role == 'ceo' || user.role == 'packing' || user.permission == 'admin',
        delete: user.role == 'office-manager' || user.role == 'ceo' || user.permission == 'admin'
      }
    },
    activeFilters({ filters }) {
      return Object.fromEntries(
        Object.entries(filters).filter(d => d[1] != null && d[1] != false)
      )
    }
  },
  watch: {
    'pagination.page'(page) {
      this.getSkids({ page: page });
    },
    '$route.query': {
      handler(query) {
        this.getSkids(query);
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("skidInstallations", [
      "getSkids", "updateSkid", "deleteSkid", "toggleAllSelected"
    ]),
    ...mapMutations("skidInstallations", [
      "changePage", "setFilter", "toggleSelected", "setProject"
    ]),
    initialize() {
      let query = this.$route.query;
      if (query) {
        Object.keys(query).forEach(key => {
          this.setFilter({ field: key, value: query[key] })
        })
        this.getSkids(query);
      } else {
        this.getSkids();
      }
    },
    viewSkid(skid) {
      if (this.can.create) {
        this.$router.push({ 
          name: 'SkidViewer', 
          params: { 
            skidId: skid.id,
            skid: skid
          }
        })
      } else {
        this.viewSkidStatus(skid);
      }
    },
    viewSkidStatus(skid) {
      this.$router.push({ 
        name: 'InstallationItems', 
        query: { skid_no: skid.skid_no, project_id: skid.project_id, per_page: 1000 }
      })
    },
    changeFilter(field, value) {
      this.setFilter({ field: field, value: value });
      let query = Object.assign({}, this.$route.query);
      if (value) {  
        query[field] = value;
        this.$router.push({ name: 'SkidList', query: query })
      } else {
        delete query[field];
        this.$router.push({ name: 'SkidList', query: query })
      }
    },
    launchConfirmDelete(id) {
      if (id) {
        this.deleteConfirmedCb = () => {
          this.deleteSkid(id).then(
            () => this.confirmDelete.state = false
          );
        };
      } else {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteSkid(id);
          });
          this.confirmDelete.state = false;
        };
      }
      this.confirmDelete.state = true;
    },
    download() {
      this.downloadCsv({ 
        data: this.skids.map(d => {
          let res = {}
          res['Project Name'] = d.project.project_name;

          let otherFields = [
            { field: 'skid_no', label: 'Skid/ Group #' },
            { field: 'desc', label: 'Description' },
            { field: 'packed_on', label: 'Packed On', format: d => this.format(this.parse(d)) },
            { field: 'sent_on', label: 'Sent On', format: d => this.format(this.parse(d)) },
            { field: 'no_items', label: '# of Items' },
            { field: 'total_sqft', label: 'Total SQFT' },
            { field: 'installed_sqft', label: 'Installed SQFT' }
          ]
          
          otherFields.forEach(o => {
            res[o.label] = o.format ? o.format(d[o.field]) : d[o.field]
          })

          res['Installation Progress %'] = this.$options.filters.percentage(d.installed_sqft / d.total_sqft);
          res['Created By'] = d.created_user.display_name;
          res['Created On'] = this.format(this.parse(d.created_on));

          return res
        }), 
        filename: `defected_items_${this.formatCsvDate(new Date)}.csv` 
      });
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
@import '@/styles/responsive.scss';
</style>